export const SUPPORTED = ['en', 'zh', 'ja'];

/**
 * Default language is always English unless the user previously chose one.
 * @returns {'en'|'zh'|'ja'}
 */
export function getDefaultLanguage() {
  try {
    const stored = typeof localStorage !== 'undefined' && localStorage.getItem('language');
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (e) {
    // localStorage not available
  }
  return 'en';
}

export default {
  en: {
    language: {
      title: 'Language',
      choose: 'Choose display language',
      english: 'English',
      chinese: '中文',
      japanese: '日本語',
      enDesc: 'English interface',
      zhDesc: '简体中文界面',
      jaDesc: '日本語インターフェース'
    },
    theme: {
      dark: 'Dark',
      light: 'Light',
      darkDesc: 'Night theme',
      lightDesc: 'Day theme',
      toLight: 'Switch to light mode',
      toDark: 'Switch to dark mode'
    },
    header: {
      signedIn: 'Signed in',
      createUser: 'Create User',
      createUserDesc: 'Add username, email and avatar',
      userList: 'User List',
      userListDesc: 'Browse and manage accounts',
      guest: 'Guest'
    },
    sidebar: {
      brand: 'Vulnerability',
      brandSub: 'Security intelligence',
      workspace: 'Workspace',
      home: 'Home',
      homeDesc: 'Workspace overview',
      analysis: 'Analysis',
      analysisDesc: 'CVE data sources',
      nvd: 'CVE NVD',
      nvdDesc: 'NIST vulnerability DB',
      mitre: 'CVE MITRE',
      mitreDesc: 'CVE dictionary',
      cisa: 'CVE CISA',
      cisaDesc: 'Known exploited vulns',
      github: 'CVE GitHub',
      githubDesc: 'Security advisories',
      exploited: 'CVE Exploited',
      exploitedDesc: 'Exploit-DB records',
      packages: 'Packages',
      packagesDesc: 'Package security tools',
      vulnerabilityCheck: 'Vulnerability Check',
      vulnerabilityCheckDesc: 'Scan packages for known vulns',
      cveCheck: 'CVE Check',
      cveCheckDesc: 'Find packages by CVE ID',
      history: 'History',
      historyDesc: 'Batch and download records',
      batchHistory: 'Batch History',
      batchHistoryDesc: 'View batch job runs',
      downloadHistory: 'Download History',
      downloadHistoryDesc: 'App download IP, machine, time',
      app: 'App',
      appDesc: 'Desktop app downloads',
      wgmu: 'WGMU',
      wgmuDesc: 'Multi-tunnel WireGuard',
      logout: 'Logout',
      logoutDesc: 'Sign out of this session',
      hideMenu: 'Hide menu',
      showMenu: 'Show menu',
      loggedOut: 'Logged out successfully'
    },
    cveCheck: {
      cveId: 'CVE ID',
      cveIdPlaceholder: 'e.g. CVE-2000-0062',
      search: 'Search',
      clear: 'Clear',
      enterCve: 'Enter a CVE ID to search',
      emptyHint:
        'Type a CVE ID and click Search to match packages across NVD, MITRE, CISA, and GitHub.',
      loading: 'Checking CVE packages…',
      notFound: 'This CVE was not found in any source.',
      noResultsYet: 'No results yet',
      noPackages: 'No packages reported for this CVE.',
      loadFailed: 'Failed to load CVE package check',
      packagesFound: '{n} packages matched',
      foundSources: 'Found in sources',
      missingSources: 'Missing sources',
      noneMissing: 'None',
      packageCount: 'Packages',
      matchedPackages: 'Matched packages',
      sourceFound: 'Found',
      sourceMissing: 'Missing',
      vendor: 'Vendor',
      product: 'Product',
      packageName: 'Package name',
      versions: 'Versions',
      pkgCountShort: '{n} pkgs',
      noVersions: 'No version range',
      noPackagesInSource: 'No packages listed for this source.',
      copyCveId: 'Copy CVE ID',
      cveIdCopied: 'CVE ID copied'
    },
    batchHistory: {
      batchName: 'Batch name',
      batchNamePlaceholder: 'e.g. mitre, nvd, cve_mitre',
      status: 'Status',
      statusAll: 'All',
      statusSuccess: 'Success',
      statusError: 'Error',
      statusPending: 'Pending',
      statusRunning: 'Running',
      date: 'Date',
      search: 'Search',
      clear: 'Clear',
      showing: 'Showing',
      of: 'of',
      noResultsYet: 'No results yet',
      loading: 'Loading batch history…',
      noMatch: 'No batch history matched your filters.',
      resetFilters: 'Reset filters',
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      showLess: 'Show less',
      showMore: 'Show more',
      loadFailed: 'Failed to load batch history',
      colBatchName: 'Batch name',
      colVersion: 'Version',
      colDate: 'Date',
      colStatus: 'Status',
      colStart: 'Start time',
      colEnd: 'End time',
      colRunTime: 'Run time',
      colResult: 'Execution result',
      colLogs: 'Logs',
      viewLogs: 'View logs',
      logsKicker: 'Batch log',
      loadingLogs: 'Loading logs…',
      loadLogsFailed: 'Failed to load batch logs',
      logEmpty: 'No log content.',
      logMissing: 'Log file not found',
      logTruncated: 'Log truncated',
      copyLogs: 'Copy',
      copied: 'Copied'
    },
    downloadHistory: {
      product: 'Product',
      productPlaceholder: 'e.g. wgmu',
      ip: 'IP',
      ipPlaceholder: 'e.g. 203.0.113.8',
      machine: 'Machine',
      machinePlaceholder: 'e.g. macOS',
      region: 'Region',
      regionPlaceholder: 'e.g. Tokyo, Japan',
      date: 'Date',
      search: 'Search',
      clear: 'Clear',
      downloadSum: 'Downloads',
      showing: 'Showing',
      of: 'of',
      noResultsYet: 'No results yet',
      loading: 'Loading download history…',
      noMatch: 'No downloads matched your filters.',
      resetFilters: 'Reset filters',
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      loadFailed: 'Failed to load download history',
      colIp: 'IP',
      colRegion: 'Region',
      colMachine: 'Machine',
      colTime: 'Time',
      colProduct: 'Product',
      colVersion: 'Version',
      colFilename: 'File'
    },
    home: {
      brand: 'Vulnerability',
      brandSub: 'Security intelligence',
      headline: 'See risk before it spreads',
      lead: 'One workspace for NVD, MITRE, CISA, and GitHub advisories — search, filter, and act.',
      openNvd: 'Open NVD feed',
      browseSources: 'Browse sources',
      chooseSource: 'Choose a source',
      chooseSourceDesc: 'Jump straight into the feed you need.',
      toolsTitle: 'Package tools',
      toolsDesc: 'Scan packages against known vulnerabilities.',
      nvdDesc: 'NIST National Vulnerability Database',
      mitreDesc: 'MITRE CVE dictionary',
      cisaDesc: 'CISA Known Exploited Vulnerabilities',
      githubDesc: 'GitHub Security Advisories',
      exploitedDesc: 'Exploit-DB public exploits'
    },
    wgmu: {
      name: 'wireguard-muti',
      platform: 'macOS client',
      headline: 'Keep several WireGuard tunnels up at once',
      lead: 'The official WireGuard app on Mac only allows one tunnel. wireguard-muti talks to Homebrew wg / wg-quick instead, so each config becomes its own utun interface.',
      download: 'Download',
      downloadUnavailable: 'Download is available on the server build',
      downloadHint: 'The file is a zip. Unzip it, then open wireguard-muti.app.',
      downloadSum: 'Downloads',
      downloadStatsTitle: 'Download history',
      downloadStatsEmpty: 'No downloads recorded yet',
      openHistory: 'Open download history',
      downloadIp: 'IP',
      downloadMachine: 'Machine',
      downloadTime: 'Time',
      openIntro: 'Open introduce page',
      featuresTitle: 'What it does',
      featuresDesc: 'A Mac GUI for multiple WireGuard tunnels, keys, and live rates.',
      featureMultiTitle: 'Multiple tunnels',
      featureMultiText:
        'Several tunnels can stay connected at the same time. Warns if two connected tunnels both claim 0.0.0.0/0.',
      featureToolsTitle: 'Checks wireguard-tools',
      featureToolsText:
        'Looks for wg and wg-quick on this Mac. If they are missing, it can install them with Homebrew.',
      featureHelperTitle: 'One-time helper',
      featureHelperText:
        'Installs an administrator helper so start/stop does not ask for a password every time.',
      featureTunnelsTitle: 'List and switch',
      featureTunnelsText:
        'Lists every wgN.conf / key pair. Start or stop each one, or connect all.',
      featureManageTitle: 'Create and import',
      featureManageText:
        'Create a new key, import a .conf, edit Address / DNS / peers, or drop a config onto the window.',
      featureMenubarTitle: 'Menu bar extra',
      featureMenubarText: 'Live rates in the menu bar while a tunnel is up.',
      requirementsTitle: 'Requirements',
      reqMacos: 'macOS 14 or later',
      reqHomebrew: 'Homebrew',
      reqTools: 'wireguard-tools (brew install wireguard-tools)',
      notSubstitute:
        'This GUI is not a substitute for wg / wg-quick, and it does not talk to the App Store WireGuard app. Keys and configs live in /opt/homebrew/etc/wireguard.',
      firstRunTitle: 'First run',
      step1: 'Open wireguard-muti.',
      step2: 'Confirm WireGuard (with the WireGuard icon) and continue.',
      step3: 'If tools are missing, use Install wireguard-tools (needs Homebrew).',
      step4: 'Install the helper when the banner asks (one administrator prompt).',
      step5: 'Add or import a tunnel, then use the switch to connect.',
      networkTitle: 'Local network',
      networkLead:
        'Turning a tunnel on or off does not change the Mac’s Wi-Fi or LAN address. Same-subnet devices usually stay reachable. Internet routing and DNS can change, depending on the config:',
      network1:
        'AllowedIPs for a tunnel subnet only (for example 10.8.0.0/24) leaves the LAN alone',
      network2: 'AllowedIPs = 0.0.0.0/0 sends internet through the tunnel',
      network3: 'AllowedIPs that overlap your LAN can capture local traffic',
      network4: 'DNS = in the conf is applied to the Mac’s network service by wg-quick'
    },
    pkgCheck: {
      package: 'Package',
      packagePlaceholder: 'e.g. next.js',
      packageRequired: 'Please enter a package name',
      sources: 'Sources',
      sourcesAll: 'All sources',
      search: 'Search',
      clear: 'Clear',
      showing: 'Showing',
      of: 'of',
      noResultsYet: 'No results yet',
      enterPackage: 'Enter a package to search',
      emptyHint: 'Type a package name and click Search to check known vulnerabilities.',
      noMatch: 'No vulnerabilities found for this package.',
      loading: 'Checking package vulnerabilities…',
      loadFailed: 'Failed to load package vulnerabilities',
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      usable: 'Usable',
      notUsable: 'Not usable',
      blockReason: 'Reason',
      blockAdvise: 'Advise',
      blockSafeVersion: 'Version you can use',
      blockVulnVersion: 'Vulnerable versions',
      useOrLater: 'or later',
      noPatchedVersion: 'No fixed version reported',
      occurredReason: 'Occurred because',
      releaseTime: 'Release time',
      publishedTime: 'Published time',
      lastModifiedTime: 'Last modified time',
      attackCodeSummary: 'Attack code',
      attackCodeJumpHint: 'Jump to CVE cards with attack code'
    },
    cve: {
      keyword: 'Keyword',
      keywordPlaceholder: 'e.g. osticket, XSS',
      keywordPlaceholderExploited: 'e.g. CVE-2009-3699 or buffer overflow',
      severity: 'Severity',
      type: 'Type',
      advisoryType: 'Advisory type',
      advisoryTypeAll: 'ALL',
      advisoryReviewed: 'reviewed',
      advisoryUnreviewed: 'unreviewed',
      source: 'Source',
      sourcePlaceholder: 'e.g. php, python, java',
      sourcePlaceholderGithub: 'e.g. npm, pip, maven, go',
      sourcePlaceholderExploited: 'e.g. linux, windows, php',
      search: 'Search',
      clear: 'Clear',
      showing: 'Showing',
      of: 'of',
      noResultsYet: 'No results yet',
      explain: 'Explain',
      explainTitle: 'What is this page?',
      aboutPage: 'About this page',
      gotIt: 'Got it',
      prev: 'Prev',
      next: 'Next',
      page: 'Page',
      loading: 'Loading vulnerability intel…',
      noMatch: 'No vulnerabilities matched your filters.',
      resetFilters: 'Reset filters',
      showLess: 'Show less',
      showMore: 'Show more',
      moreCount: '+{n} more',
      colId: 'CVE ID',
      colExploitId: 'CVE / EDB',
      copyCveId: 'Copy CVE ID',
      cveIdCopied: 'CVE ID copied',
      colSeverity: 'Severity',
      colType: 'Type',
      colScore: 'Score',
      colVerified: 'Verified',
      verifiedYes: 'Yes',
      verifiedNo: 'No',
      colCvss: 'CVSS',
      colAuthor: 'Author',
      colCwes: 'CWEs',
      colEdb: 'EDB ID',
      colPackages: 'Packages',
      colSource: 'Source',
      colLanguages: 'Languages',
      colPlatform: 'Platform',
      colAdvisoryType: 'Type',
      colPublished: 'Published',
      colDescription: 'Description',
      explainNvdTitle: 'CVE NVD',
      explainNvdSummary: 'Browse vulnerabilities from the NIST National Vulnerability Database.',
      explainNvd1: 'NVD publishes CVEs with CVSS scores, severity, CWEs, and CPE package matches.',
      explainNvd2: 'Use Keyword to search titles and descriptions (e.g. product name or XSS).',
      explainNvd3: 'Filter by Severity (CRITICAL / HIGH / MEDIUM / LOW) or Source language hints.',
      explainNvd4: 'Open a CVE ID to view the official NVD detail page.',
      explainMitreTitle: 'CVE MITRE',
      explainMitreSummary:
        'Explore the MITRE CVE dictionary — the authoritative list of CVE identifiers.',
      explainMitre1: 'MITRE assigns and maintains CVE IDs used across the security industry.',
      explainMitre2: 'This view helps you look up CVE records and related weakness context.',
      explainMitre3: 'Search by keyword to find matching CVE entries quickly.',
      explainMitre4: 'Use this page when you need the canonical CVE identity for a vulnerability.',
      explainCisaTitle: 'CVE CISA',
      explainCisaSummary:
        'Track CISA Known Exploited Vulnerabilities (KEV) — issues actively abused in the wild.',
      explainCisa1: 'CISA KEV highlights CVEs that are known to be exploited by attackers.',
      explainCisa2: 'Prioritize these for patching — they carry higher real-world risk.',
      explainCisa3: 'Filter and search to focus on vendors, products, or severity of concern.',
      explainCisa4: 'Use this feed for operational triage and remediation planning.',
      explainGithubTitle: 'CVE GitHub',
      explainGithubSummary:
        'Review GitHub Security Advisories tied to open-source packages and ecosystems.',
      explainGithub1:
        'Advisories include affected packages, ecosystems (npm, pip, maven, go, …), and ranges.',
      explainGithub2: 'Source filter works well with ecosystem names such as npm, pip, or maven.',
      explainGithub3: 'Package chips show the vulnerable dependency context from each advisory.',
      explainGithub4: 'Use this page when assessing risk in your software supply chain.',
      explainExploitedTitle: 'CVE Exploited',
      explainExploitedSummary: 'Browse public exploit records from Exploit-DB (cve_exploited_db).',
      explainExploited1:
        'Each row is an exploit entry with type, platform/source, author, and verification status.',
      explainExploited2:
        'Search by CVE ID (e.g. CVE-2009-3699) or keyword; filter by type or source (linux, php, …).',
      explainExploited3: 'Open the ID link to view the exploit on Exploit-DB.',
      explainExploited4: 'Use this page to find proof-of-concept and public exploit material.'
    },
    login: {
      brand: 'Vulnerability',
      tagline: 'Security intelligence workspace',
      point1Title: 'CVE analysis',
      point1Desc: 'NVD, MITRE, CISA, GitHub',
      point2Title: 'Threat visibility',
      point2Desc: 'Severity, packages, advisories',
      signIn: 'Sign in',
      subtitle: 'Use your account to continue.',
      username: 'Username',
      usernamePlaceholder: 'Email or user ID',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      verification: 'Verification',
      solve: 'Solve',
      answer: 'Answer',
      refreshCaptcha: 'Refresh verification',
      remember: 'Remember me',
      signingIn: 'Signing in…',
      defaultMessage: 'Please login to use this page.',
      usernameRequired: 'Please enter username',
      passwordRequired: 'Please enter password',
      captchaWrong: 'Verification answer is incorrect',
      loginFailed: 'Login failed',
      noToken: 'Login succeeded but no access token was returned'
    },
    createUser: {
      title: 'Create User',
      desc: 'Add a new account with a temporary password and header picture.',
      tip1: 'Temporary password for first login',
      tip2: 'User must reset password on sign-in',
      tip3: 'Avatar appears in the top header',
      viewList: 'View user list',
      headerPic: 'Header picture',
      headerPicHint: 'PNG / JPG · shown after login',
      select: 'Select',
      clear: 'Clear',
      username: 'Username',
      usernamePlaceholder: 'userid',
      email: 'Email',
      emailPlaceholder: 'mail@example.com',
      password: 'Password (first time)',
      passwordPlaceholder: 'At least 6 characters',
      note: 'The user will be asked to set a new password on first login.',
      creating: 'Creating…',
      submit: 'Create User'
    },
    userList: {
      title: 'User List',
      subtitle: 'All registered users',
      createUser: 'Create User',
      empty: 'No users found',
      admin: 'Admin',
      user: 'User',
      reset: 'Reset',
      yes: 'Yes',
      no: 'No',
      active: 'Active',
      inactive: 'Inactive',
      created: 'Created',
      firstLogin: 'First login',
      updated: 'Updated',
      delete: 'Delete',
      activate: 'Active'
    },
    attackCode: {
      button: 'Attack code',
      title: 'Attack code',
      kicker: 'Proof of concept',
      loading: 'Loading attack code…',
      empty: 'No attack code content returned.',
      unavailable: 'Attack code is not available for this CVE.',
      loadFailed: 'Failed to load attack code',
      close: 'Close',
      copy: 'Copy',
      copied: 'Copied',
      summary: 'Summary',
      exploits: 'Exploit references',
      locations: 'Vulnerable code locations',
      references: 'References',
      exploitTag: 'Exploit',
      lines: 'Lines',
      function: 'Function',
      snippets: 'Code snippets',
      rawSource: 'Raw PoC',
      locationHint: 'Location hint',
      rawLink: 'Raw',
      sourceLink: 'Source',
      codeFetched: 'Code fetched',
      fetchFailed: 'Fetch failed',
      sourceLocations: 'Source code locations',
      cisaContext: 'CISA context',
      requiredAction: 'Required action',
      ransomware: 'Ransomware use',
      availCode: 'PoC code available',
      availReference: 'References only',
      availKnown: 'Known exploited',
      availNone: 'No PoC code'
    },
    common: {
      na: 'N/A'
    }
  },
  zh: {
    language: {
      title: '语言',
      choose: '选择显示语言',
      english: 'English',
      chinese: '中文',
      japanese: '日本語',
      enDesc: '英文界面',
      zhDesc: '简体中文界面',
      jaDesc: '日语界面'
    },
    theme: {
      dark: '深色',
      light: '浅色',
      darkDesc: '夜间主题',
      lightDesc: '日间主题',
      toLight: '切换到浅色模式',
      toDark: '切换到深色模式'
    },
    header: {
      signedIn: '已登录',
      createUser: '创建用户',
      createUserDesc: '添加用户名、邮箱和头像',
      userList: '用户列表',
      userListDesc: '浏览和管理账号',
      guest: '访客'
    },
    sidebar: {
      brand: '漏洞情报',
      brandSub: '安全情报工作台',
      workspace: '工作区',
      home: '首页',
      homeDesc: '工作区概览',
      analysis: '分析',
      analysisDesc: 'CVE 数据源',
      nvd: 'CVE NVD',
      nvdDesc: 'NIST 漏洞数据库',
      mitre: 'CVE MITRE',
      mitreDesc: 'CVE 字典',
      cisa: 'CVE CISA',
      cisaDesc: '已知被利用漏洞',
      github: 'CVE GitHub',
      githubDesc: '安全公告',
      exploited: 'CVE Exploited',
      exploitedDesc: 'Exploit-DB 记录',
      packages: '软件包',
      packagesDesc: '软件包安全工具',
      vulnerabilityCheck: '漏洞检查',
      vulnerabilityCheckDesc: '扫描已知漏洞',
      cveCheck: 'CVE 检查',
      cveCheckDesc: '按 CVE ID 查找软件包',
      history: '历史记录',
      historyDesc: '批处理与下载记录',
      batchHistory: '批处理历史',
      batchHistoryDesc: '查看批处理任务运行记录',
      downloadHistory: '下载历史',
      downloadHistoryDesc: '应用下载 IP、设备与时间',
      app: '应用',
      appDesc: '桌面应用下载',
      wgmu: 'WGMU',
      wgmuDesc: '多隧道 WireGuard',
      logout: '退出登录',
      logoutDesc: '退出当前会话',
      hideMenu: '隐藏菜单',
      showMenu: '显示菜单',
      loggedOut: '已成功退出登录'
    },
    cveCheck: {
      cveId: 'CVE ID',
      cveIdPlaceholder: '例如 CVE-2000-0062',
      search: '搜索',
      clear: '清除',
      enterCve: '输入 CVE ID 进行搜索',
      emptyHint: '输入 CVE ID 并点击搜索，匹配 NVD、MITRE、CISA、GitHub 中的软件包。',
      loading: '正在检查 CVE 软件包…',
      notFound: '未在任何数据源中找到该 CVE。',
      noResultsYet: '暂无结果',
      noPackages: '该 CVE 未报告相关软件包。',
      loadFailed: '加载 CVE 软件包检查失败',
      packagesFound: '匹配到 {n} 个软件包',
      foundSources: '已找到数据源',
      missingSources: '缺失数据源',
      noneMissing: '无',
      packageCount: '软件包数',
      matchedPackages: '匹配的软件包',
      sourceFound: '已找到',
      sourceMissing: '缺失',
      vendor: '厂商',
      product: '产品',
      packageName: '包名',
      versions: '版本',
      pkgCountShort: '{n} 个包',
      noVersions: '无版本范围',
      noPackagesInSource: '该数据源未列出软件包。',
      copyCveId: '复制 CVE ID',
      cveIdCopied: 'CVE ID 已复制'
    },
    batchHistory: {
      batchName: '批处理名称',
      batchNamePlaceholder: '例如 mitre、nvd、cve_mitre',
      status: '状态',
      statusAll: '全部',
      statusSuccess: '成功',
      statusError: '错误',
      statusPending: '待处理',
      statusRunning: '运行中',
      date: '日期',
      search: '搜索',
      clear: '清除',
      showing: '显示',
      of: '共',
      noResultsYet: '暂无结果',
      loading: '正在加载批处理历史…',
      noMatch: '没有匹配的批处理历史。',
      resetFilters: '重置筛选',
      prev: '上一页',
      next: '下一页',
      page: '页',
      showLess: '收起',
      showMore: '展开',
      loadFailed: '加载批处理历史失败',
      colBatchName: '批处理名称',
      colVersion: '版本',
      colDate: '日期',
      colStatus: '状态',
      colStart: '开始时间',
      colEnd: '结束时间',
      colRunTime: '运行时长',
      colResult: '执行结果',
      colLogs: '日志',
      viewLogs: '查看日志',
      logsKicker: '批处理日志',
      loadingLogs: '正在加载日志…',
      loadLogsFailed: '加载批处理日志失败',
      logEmpty: '暂无日志内容。',
      logMissing: '未找到日志文件',
      logTruncated: '日志已截断',
      copyLogs: '复制',
      copied: '已复制'
    },
    downloadHistory: {
      product: '产品',
      productPlaceholder: '例如 wgmu',
      ip: 'IP',
      ipPlaceholder: '例如 203.0.113.8',
      machine: '设备',
      machinePlaceholder: '例如 macOS',
      region: '地区',
      regionPlaceholder: '例如 东京, 日本',
      date: '日期',
      search: '搜索',
      clear: '清除',
      downloadSum: '下载次数',
      showing: '显示',
      of: '共',
      noResultsYet: '暂无结果',
      loading: '正在加载下载历史…',
      noMatch: '没有匹配的下载记录。',
      resetFilters: '重置筛选',
      prev: '上一页',
      next: '下一页',
      page: '页',
      loadFailed: '加载下载历史失败',
      colIp: 'IP',
      colRegion: '地区',
      colMachine: '设备',
      colTime: '时间',
      colProduct: '产品',
      colVersion: '版本',
      colFilename: '文件'
    },
    home: {
      brand: '漏洞情报',
      brandSub: '安全情报',
      headline: '在风险扩散之前看见它',
      lead: '统一查看 NVD、MITRE、CISA 与 GitHub 安全公告 — 搜索、筛选、处置。',
      openNvd: '打开 NVD 数据',
      browseSources: '浏览数据源',
      chooseSource: '选择数据源',
      chooseSourceDesc: '直接进入所需情报源。',
      toolsTitle: '软件包工具',
      toolsDesc: '扫描软件包中的已知漏洞。',
      nvdDesc: 'NIST 国家漏洞数据库',
      mitreDesc: 'MITRE CVE 字典',
      cisaDesc: 'CISA 已知被利用漏洞',
      githubDesc: 'GitHub 安全公告',
      exploitedDesc: 'Exploit-DB 公开利用'
    },
    wgmu: {
      name: 'wireguard-muti',
      platform: 'macOS 客户端',
      headline: '同时保持多条 WireGuard 隧道在线',
      lead: '官方 Mac WireGuard 应用同时只能开一条隧道。wireguard-muti 通过 Homebrew 的 wg / wg-quick 工作，每份配置各自对应一个 utun 接口。',
      download: '下载',
      downloadUnavailable: '下载仅在服务器环境可用',
      downloadHint: '下载的是 zip。解压后打开 wireguard-muti.app。',
      downloadSum: '下载次数',
      downloadStatsTitle: '下载记录',
      downloadStatsEmpty: '暂无下载记录',
      openHistory: '打开下载历史',
      downloadIp: 'IP',
      downloadMachine: '设备',
      downloadTime: '时间',
      openIntro: '在新标签页打开介绍',
      featuresTitle: '功能',
      featuresDesc: '用于多隧道、密钥和实时速率的 Mac 界面。',
      featureMultiTitle: '多隧道',
      featureMultiText: '可以同时连接多条隧道。若两条已连接隧道都声明 0.0.0.0/0，会发出警告。',
      featureToolsTitle: '检查 wireguard-tools',
      featureToolsText: '检测本机是否有 wg 和 wg-quick。若缺失，可通过 Homebrew 安装。',
      featureHelperTitle: '一次性助手',
      featureHelperText: '安装管理员助手后，启动/停止隧道无需每次输入密码。',
      featureTunnelsTitle: '列表与开关',
      featureTunnelsText: '列出所有 wgN.conf / 密钥对。可单独启停，或全部连接。',
      featureManageTitle: '创建与导入',
      featureManageText: '创建新密钥、导入 .conf、编辑 Address / DNS / peers，或将配置拖到窗口。',
      featureMenubarTitle: '菜单栏',
      featureMenubarText: '隧道运行时在菜单栏显示实时速率。',
      requirementsTitle: '运行要求',
      reqMacos: 'macOS 14 或更高版本',
      reqHomebrew: 'Homebrew',
      reqTools: 'wireguard-tools（brew install wireguard-tools）',
      notSubstitute:
        '此界面不能替代 wg / wg-quick，也不会对接 App Store 的 WireGuard 应用。密钥与配置位于 /opt/homebrew/etc/wireguard。',
      firstRunTitle: '首次运行',
      step1: '打开 wireguard-muti。',
      step2: '确认 WireGuard（带 WireGuard 图标）并继续。',
      step3: '若工具缺失，使用 Install wireguard-tools（需要 Homebrew）。',
      step4: '按横幅提示安装助手（一次管理员授权）。',
      step5: '添加或导入隧道，然后用开关连接。',
      networkTitle: '本地网络',
      networkLead:
        '开关隧道不会改变 Mac 的 Wi-Fi 或局域网地址。同网段设备通常仍可访问。互联网路由和 DNS 可能随配置变化：',
      network1: 'AllowedIPs 仅包含隧道子网（例如 10.8.0.0/24）时，局域网不受影响',
      network2: 'AllowedIPs = 0.0.0.0/0 会将互联网流量送入隧道',
      network3: 'AllowedIPs 与局域网重叠时可能接管本地流量',
      network4: 'conf 中的 DNS = 会由 wg-quick 应用到 Mac 的网络服务'
    },
    pkgCheck: {
      package: '软件包',
      packagePlaceholder: '例如 next.js',
      packageRequired: '请输入软件包名称',
      sources: '数据源',
      sourcesAll: '全部来源',
      search: '搜索',
      clear: '清除',
      showing: '显示',
      of: '共',
      noResultsYet: '暂无结果',
      enterPackage: '输入软件包名称进行搜索',
      emptyHint: '输入软件包名称并点击搜索，查看已知漏洞。',
      noMatch: '未找到该软件包的相关漏洞。',
      loading: '正在检查软件包漏洞…',
      loadFailed: '加载软件包漏洞失败',
      prev: '上一页',
      next: '下一页',
      page: '页',
      usable: '可用',
      notUsable: '不可用',
      blockReason: '原因',
      blockAdvise: '建议',
      blockSafeVersion: '可用版本',
      blockVulnVersion: '漏洞版本',
      useOrLater: '或更高',
      noPatchedVersion: '未提供修复版本',
      occurredReason: '发生原因',
      releaseTime: '发布时间',
      publishedTime: '发布时间',
      lastModifiedTime: '最后修改时间',
      attackCodeSummary: '攻击代码',
      attackCodeJumpHint: '跳转到含攻击代码的 CVE 卡片'
    },
    cve: {
      keyword: '关键词',
      keywordPlaceholder: '例如 osticket、XSS',
      keywordPlaceholderExploited: '例如 CVE-2009-3699 或 buffer overflow',
      severity: '严重级别',
      type: '类型',
      advisoryType: '公告类型',
      advisoryTypeAll: '全部',
      advisoryReviewed: 'reviewed',
      advisoryUnreviewed: 'unreviewed',
      source: '来源',
      sourcePlaceholder: '例如 php、python、java',
      sourcePlaceholderGithub: '例如 npm、pip、maven、go',
      sourcePlaceholderExploited: '例如 linux、windows、php',
      search: '搜索',
      clear: '清除',
      showing: '显示',
      of: '共',
      noResultsYet: '暂无结果',
      explain: '说明',
      explainTitle: '本页是什么？',
      aboutPage: '关于本页',
      gotIt: '知道了',
      prev: '上一页',
      next: '下一页',
      page: '页',
      loading: '正在加载漏洞情报…',
      noMatch: '没有符合筛选条件的漏洞。',
      resetFilters: '重置筛选',
      showLess: '收起',
      showMore: '展开',
      moreCount: '+{n} 更多',
      colId: 'CVE ID',
      colExploitId: 'CVE / EDB',
      copyCveId: '复制 CVE ID',
      cveIdCopied: 'CVE ID 已复制',
      colSeverity: '严重级别',
      colType: '类型',
      colScore: '评分',
      colVerified: '已验证',
      verifiedYes: '是',
      verifiedNo: '否',
      colCvss: 'CVSS',
      colAuthor: '作者',
      colCwes: 'CWE',
      colEdb: 'EDB ID',
      colPackages: '受影响包',
      colSource: '来源',
      colLanguages: '语言/生态',
      colPlatform: '平台',
      colAdvisoryType: '类型',
      colPublished: '发布日期',
      colDescription: '描述',
      explainNvdTitle: 'CVE NVD',
      explainNvdSummary: '浏览 NIST 国家漏洞数据库中的漏洞信息。',
      explainNvd1: 'NVD 提供带有 CVSS 评分、严重级别、CWE 与 CPE 包匹配的 CVE。',
      explainNvd2: '使用关键词搜索标题与描述（例如产品名或 XSS）。',
      explainNvd3: '可按严重级别（CRITICAL / HIGH / MEDIUM / LOW）或来源语言筛选。',
      explainNvd4: '点击 CVE ID 可打开官方 NVD 详情页。',
      explainMitreTitle: 'CVE MITRE',
      explainMitreSummary: '浏览 MITRE CVE 字典 — CVE 标识符的权威列表。',
      explainMitre1: 'MITRE 负责分配并维护安全行业使用的 CVE ID。',
      explainMitre2: '此视图便于查阅 CVE 记录及相关弱点上下文。',
      explainMitre3: '通过关键词快速查找匹配的 CVE 条目。',
      explainMitre4: '需要确认漏洞的规范 CVE 标识时使用本页。',
      explainCisaTitle: 'CVE CISA',
      explainCisaSummary: '跟踪 CISA 已知被利用漏洞（KEV）— 已在野外被主动利用的问题。',
      explainCisa1: 'CISA KEV 突出显示已知被攻击者利用的 CVE。',
      explainCisa2: '应优先修复这些漏洞 — 它们具有更高的现实风险。',
      explainCisa3: '可筛选搜索，聚焦关注的厂商、产品或严重级别。',
      explainCisa4: '适用于运营分诊与修复计划。',
      explainGithubTitle: 'CVE GitHub',
      explainGithubSummary: '查看与开源包及生态系统相关的 GitHub 安全公告。',
      explainGithub1: '公告包含受影响的包、生态系统（npm、pip、maven、go 等）及版本范围。',
      explainGithub2: '来源筛选适合使用生态系统名称，如 npm、pip 或 maven。',
      explainGithub3: '包标签展示每条公告中的脆弱依赖上下文。',
      explainGithub4: '评估软件供应链风险时使用本页。',
      explainExploitedTitle: 'CVE Exploited',
      explainExploitedSummary: '浏览 Exploit-DB 公开利用记录（cve_exploited_db）。',
      explainExploited1: '每行是一条利用记录，包含类型、平台/来源、作者与验证状态。',
      explainExploited2: '可按 CVE ID（如 CVE-2009-3699）或关键词搜索，并按类型或来源筛选。',
      explainExploited3: '点击 ID 链接可在 Exploit-DB 查看该利用。',
      explainExploited4: '用于查找公开 PoC 与利用材料。'
    },
    login: {
      brand: '漏洞情报',
      tagline: '安全情报工作台',
      point1Title: 'CVE 分析',
      point1Desc: 'NVD、MITRE、CISA、GitHub',
      point2Title: '威胁可见性',
      point2Desc: '严重级别、软件包、公告',
      signIn: '登录',
      subtitle: '使用您的账号继续。',
      username: '用户名',
      usernamePlaceholder: '邮箱或用户 ID',
      password: '密码',
      passwordPlaceholder: '请输入密码',
      verification: '验证',
      solve: '计算',
      answer: '答案',
      refreshCaptcha: '刷新验证',
      remember: '记住我',
      signingIn: '登录中…',
      defaultMessage: '请先登录以使用本页面。',
      usernameRequired: '请输入用户名',
      passwordRequired: '请输入密码',
      captchaWrong: '验证答案不正确',
      loginFailed: '登录失败',
      noToken: '登录成功但未返回访问令牌'
    },
    createUser: {
      title: '创建用户',
      desc: '使用临时密码和头像创建新账号。',
      tip1: '首次登录使用临时密码',
      tip2: '登录后需重置密码',
      tip3: '头像显示在顶部栏',
      viewList: '查看用户列表',
      headerPic: '头像图片',
      headerPicHint: 'PNG / JPG · 登录后显示',
      select: '选择',
      clear: '清除',
      username: '用户名',
      usernamePlaceholder: 'userid',
      email: '邮箱',
      emailPlaceholder: 'mail@example.com',
      password: '密码（首次）',
      passwordPlaceholder: '至少 6 个字符',
      note: '用户首次登录时将被要求设置新密码。',
      creating: '创建中…',
      submit: '创建用户'
    },
    userList: {
      title: '用户列表',
      subtitle: '所有已注册用户',
      createUser: '创建用户',
      empty: '未找到用户',
      admin: '管理员',
      user: '用户',
      reset: '需重置',
      yes: '是',
      no: '否',
      active: '启用',
      inactive: '停用',
      created: '创建时间',
      firstLogin: '首次登录',
      updated: '更新时间',
      delete: '删除',
      activate: '启用'
    },
    attackCode: {
      button: '攻击代码',
      title: '攻击代码',
      kicker: '概念验证',
      loading: '正在加载攻击代码…',
      empty: '未返回攻击代码内容。',
      unavailable: '该 CVE 暂无攻击代码。',
      loadFailed: '加载攻击代码失败',
      close: '关闭',
      copy: '复制',
      copied: '已复制',
      summary: '摘要',
      exploits: '利用参考',
      locations: '脆弱代码位置',
      references: '参考链接',
      exploitTag: 'Exploit',
      lines: '行号',
      function: '函数',
      snippets: '代码片段',
      rawSource: '原始 PoC',
      locationHint: '位置提示',
      rawLink: '原始',
      sourceLink: '来源',
      codeFetched: '已获取代码',
      fetchFailed: '获取失败',
      sourceLocations: '源码位置',
      cisaContext: 'CISA 信息',
      requiredAction: '要求措施',
      ransomware: '勒索软件使用',
      availCode: '有 PoC 代码',
      availReference: '仅有参考链接',
      availKnown: '已知被利用',
      availNone: '无 PoC 代码'
    },
    common: {
      na: '无'
    }
  },
  ja: {
    language: {
      title: '言語',
      choose: '表示言語を選択',
      english: 'English',
      chinese: '中文',
      japanese: '日本語',
      enDesc: '英語インターフェース',
      zhDesc: '中国語インターフェース',
      jaDesc: '日本語インターフェース'
    },
    theme: {
      dark: 'ダーク',
      light: 'ライト',
      darkDesc: 'ナイトテーマ',
      lightDesc: 'デイテーマ',
      toLight: 'ライトモードに切替',
      toDark: 'ダークモードに切替'
    },
    header: {
      signedIn: 'サインイン中',
      createUser: 'ユーザー作成',
      createUserDesc: 'ユーザー名・メール・アバターを追加',
      userList: 'ユーザー一覧',
      userListDesc: 'アカウントの閲覧と管理',
      guest: 'ゲスト'
    },
    sidebar: {
      brand: 'Vulnerability',
      brandSub: 'セキュリティインテリジェンス',
      workspace: 'ワークスペース',
      home: 'ホーム',
      homeDesc: '概要',
      analysis: '分析',
      analysisDesc: 'CVE データソース',
      nvd: 'CVE NVD',
      nvdDesc: 'NIST 脆弱性 DB',
      mitre: 'CVE MITRE',
      mitreDesc: 'CVE 辞書',
      cisa: 'CVE CISA',
      cisaDesc: '既知の悪用脆弱性',
      github: 'CVE GitHub',
      githubDesc: 'セキュリティ勧告',
      exploited: 'CVE Exploited',
      exploitedDesc: 'Exploit-DB レコード',
      packages: 'パッケージ',
      packagesDesc: 'パッケージセキュリティ',
      vulnerabilityCheck: '脆弱性チェック',
      vulnerabilityCheckDesc: '既知の脆弱性をスキャン',
      cveCheck: 'CVE チェック',
      cveCheckDesc: 'CVE ID からパッケージを検索',
      history: '履歴',
      historyDesc: 'バッチとダウンロード記録',
      batchHistory: 'バッチ履歴',
      batchHistoryDesc: 'バッチジョブの実行を確認',
      downloadHistory: 'ダウンロード履歴',
      downloadHistoryDesc: 'アプリの IP・端末・時刻',
      app: 'アプリ',
      appDesc: 'デスクトップアプリ',
      wgmu: 'WGMU',
      wgmuDesc: '複数トンネル WireGuard',
      logout: 'ログアウト',
      logoutDesc: 'このセッションから退出',
      hideMenu: 'メニューを隠す',
      showMenu: 'メニューを表示',
      loggedOut: 'ログアウトしました'
    },
    cveCheck: {
      cveId: 'CVE ID',
      cveIdPlaceholder: '例: CVE-2000-0062',
      search: '検索',
      clear: 'クリア',
      enterCve: 'CVE ID を入力して検索',
      emptyHint: 'CVE ID を入力して検索し、NVD / MITRE / CISA / GitHub のパッケージを照合します。',
      loading: 'CVE パッケージを確認中…',
      notFound: 'いずれのソースにもこの CVE は見つかりませんでした。',
      noResultsYet: '結果はまだありません',
      noPackages: 'この CVE に報告されたパッケージはありません。',
      loadFailed: 'CVE パッケージ確認の読み込みに失敗しました',
      packagesFound: '{n} 件のパッケージが一致',
      foundSources: '見つかったソース',
      missingSources: '不足しているソース',
      noneMissing: 'なし',
      packageCount: 'パッケージ数',
      matchedPackages: '一致したパッケージ',
      sourceFound: 'あり',
      sourceMissing: 'なし',
      vendor: 'ベンダー',
      product: '製品',
      packageName: 'パッケージ名',
      versions: 'バージョン',
      pkgCountShort: '{n} 件',
      noVersions: 'バージョン範囲なし',
      noPackagesInSource: 'このソースにパッケージはありません。',
      copyCveId: 'CVE ID をコピー',
      cveIdCopied: 'CVE ID をコピーしました'
    },
    batchHistory: {
      batchName: 'バッチ名',
      batchNamePlaceholder: '例: mitre, nvd, cve_mitre',
      status: 'ステータス',
      statusAll: 'すべて',
      statusSuccess: '成功',
      statusError: 'エラー',
      statusPending: '保留',
      statusRunning: '実行中',
      date: '日付',
      search: '検索',
      clear: 'クリア',
      showing: '表示',
      of: '/',
      noResultsYet: '結果はまだありません',
      loading: 'バッチ履歴を読み込み中…',
      noMatch: '条件に一致するバッチ履歴がありません。',
      resetFilters: 'フィルタをリセット',
      prev: '前へ',
      next: '次へ',
      page: 'ページ',
      showLess: '閉じる',
      showMore: 'もっと見る',
      loadFailed: 'バッチ履歴の読み込みに失敗しました',
      colBatchName: 'バッチ名',
      colVersion: 'バージョン',
      colDate: '日付',
      colStatus: 'ステータス',
      colStart: '開始時刻',
      colEnd: '終了時刻',
      colRunTime: '実行時間',
      colResult: '実行結果',
      colLogs: 'ログ',
      viewLogs: 'ログを見る',
      logsKicker: 'バッチログ',
      loadingLogs: 'ログを読み込み中…',
      loadLogsFailed: 'バッチログの読み込みに失敗しました',
      logEmpty: 'ログ内容がありません。',
      logMissing: 'ログファイルが見つかりません',
      logTruncated: 'ログは切り詰められています',
      copyLogs: 'コピー',
      copied: 'コピー済み'
    },
    downloadHistory: {
      product: '製品',
      productPlaceholder: '例: wgmu',
      ip: 'IP',
      ipPlaceholder: '例: 203.0.113.8',
      machine: '端末',
      machinePlaceholder: '例: macOS',
      region: '地域',
      regionPlaceholder: '例: 東京, 日本',
      date: '日付',
      search: '検索',
      clear: 'クリア',
      downloadSum: 'ダウンロード数',
      showing: '表示',
      of: '/',
      noResultsYet: '結果はまだありません',
      loading: 'ダウンロード履歴を読み込み中…',
      noMatch: '条件に一致するダウンロードがありません。',
      resetFilters: 'フィルタをリセット',
      prev: '前へ',
      next: '次へ',
      page: 'ページ',
      loadFailed: 'ダウンロード履歴の読み込みに失敗しました',
      colIp: 'IP',
      colRegion: '地域',
      colMachine: '端末',
      colTime: '時刻',
      colProduct: '製品',
      colVersion: 'バージョン',
      colFilename: 'ファイル'
    },
    home: {
      brand: 'Vulnerability',
      brandSub: 'セキュリティインテリジェンス',
      headline: 'リスクが広がる前に把握する',
      lead: 'NVD・MITRE・CISA・GitHub 勧告を一つのワークスペースで — 検索、絞り込み、対応。',
      openNvd: 'NVD を開く',
      browseSources: 'ソースを見る',
      chooseSource: 'ソースを選択',
      chooseSourceDesc: '必要なフィードへすぐ移動。',
      toolsTitle: 'パッケージツール',
      toolsDesc: '既知の脆弱性に対してパッケージをスキャン。',
      nvdDesc: 'NIST 国家脆弱性データベース',
      mitreDesc: 'MITRE CVE 辞書',
      cisaDesc: 'CISA 既知の悪用脆弱性',
      githubDesc: 'GitHub セキュリティ勧告',
      exploitedDesc: 'Exploit-DB 公開エクスプロイト'
    },
    wgmu: {
      name: 'wireguard-muti',
      platform: 'macOS クライアント',
      headline: '複数の WireGuard トンネルを同時に維持',
      lead: '公式の Mac WireGuard アプリはトンネルを 1 本しか使えません。wireguard-muti は Homebrew の wg / wg-quick を使うため、各設定が独自の utun インターフェースになります。',
      download: 'ダウンロード',
      downloadUnavailable: 'ダウンロードはサーバー環境でのみ利用できます',
      downloadHint: 'zip です。解凍してから wireguard-muti.app を開いてください。',
      downloadSum: 'ダウンロード数',
      downloadStatsTitle: 'ダウンロード履歴',
      downloadStatsEmpty: 'まだ記録がありません',
      openHistory: 'ダウンロード履歴を開く',
      downloadIp: 'IP',
      downloadMachine: '端末',
      downloadTime: '時刻',
      openIntro: '紹介ページを新しいタブで開く',
      featuresTitle: 'できること',
      featuresDesc: '複数トンネル、鍵、リアルタイム速度の Mac GUI。',
      featureMultiTitle: '複数トンネル',
      featureMultiText:
        '複数のトンネルを同時に接続できます。接続中の 2 本がどちらも 0.0.0.0/0 を主張する場合は警告します。',
      featureToolsTitle: 'wireguard-tools の確認',
      featureToolsText:
        'この Mac に wg と wg-quick があるか確認します。無い場合は Homebrew でインストールできます。',
      featureHelperTitle: '一度きりのヘルパー',
      featureHelperText: '管理者ヘルパーを入れると、起動/停止のたびにパスワードを求められません。',
      featureTunnelsTitle: '一覧と切替',
      featureTunnelsText: 'すべての wgN.conf / 鍵ペアを表示。個別に起動停止、または一括接続。',
      featureManageTitle: '作成と取り込み',
      featureManageText:
        '新しい鍵の作成、.conf の取り込み、Address / DNS / peers の編集、ウィンドウへのドロップ。',
      featureMenubarTitle: 'メニューバー',
      featureMenubarText: 'トンネル接続中はメニューバーにリアルタイム速度を表示します。',
      requirementsTitle: '必要条件',
      reqMacos: 'macOS 14 以降',
      reqHomebrew: 'Homebrew',
      reqTools: 'wireguard-tools（brew install wireguard-tools）',
      notSubstitute:
        'この GUI は wg / wg-quick の代替ではなく、App Store の WireGuard アプリとも通信しません。鍵と設定は /opt/homebrew/etc/wireguard にあります。',
      firstRunTitle: '初回起動',
      step1: 'wireguard-muti を開きます。',
      step2: 'WireGuard（WireGuard アイコン）を確認して続行します。',
      step3: 'ツールが無い場合は Install wireguard-tools を使います（Homebrew が必要）。',
      step4: 'バナーの案内でヘルパーをインストールします（管理者プロンプトは 1 回）。',
      step5: 'トンネルを追加または取り込み、スイッチで接続します。',
      networkTitle: 'ローカルネットワーク',
      networkLead:
        'トンネルのオン/オフは Mac の Wi-Fi や LAN アドレスを変えません。同一サブネットの機器は通常そのまま届きます。インターネット経路と DNS は設定によって変わります:',
      network1: 'AllowedIPs がトンネルサブネットのみ（例: 10.8.0.0/24）なら LAN はそのまま',
      network2: 'AllowedIPs = 0.0.0.0/0 はインターネットをトンネル経由にします',
      network3: 'AllowedIPs が LAN と重なるとローカルトラフィックを取り込めます',
      network4: 'conf の DNS = は wg-quick が Mac のネットワークサービスに適用します'
    },
    pkgCheck: {
      package: 'パッケージ',
      packagePlaceholder: '例: next.js',
      packageRequired: 'パッケージ名を入力してください',
      sources: 'ソース',
      sourcesAll: 'すべてのソース',
      search: '検索',
      clear: 'クリア',
      showing: '表示',
      of: '/',
      noResultsYet: '結果はまだありません',
      enterPackage: 'パッケージ名を入力して検索',
      emptyHint: 'パッケージ名を入力し、検索して既知の脆弱性を確認します。',
      noMatch: 'このパッケージに該当する脆弱性は見つかりませんでした。',
      loading: 'パッケージの脆弱性を確認中…',
      loadFailed: 'パッケージ脆弱性の読み込みに失敗しました',
      prev: '前へ',
      next: '次へ',
      page: 'ページ',
      usable: '利用可',
      notUsable: '利用不可',
      blockReason: '理由',
      blockAdvise: '推奨',
      blockSafeVersion: '利用可能なバージョン',
      blockVulnVersion: '脆弱なバージョン',
      useOrLater: '以降',
      noPatchedVersion: '修正バージョンの報告なし',
      occurredReason: '発生理由',
      releaseTime: 'リリース時間',
      publishedTime: '公開時間',
      lastModifiedTime: '最終更新時間',
      attackCodeSummary: '攻撃コード',
      attackCodeJumpHint: '攻撃コードがある CVE カードへ移動'
    },
    cve: {
      keyword: 'キーワード',
      keywordPlaceholder: '例: osticket, XSS',
      keywordPlaceholderExploited: '例: CVE-2009-3699 または buffer overflow',
      severity: '深刻度',
      type: 'タイプ',
      advisoryType: 'アドバイザリ種別',
      advisoryTypeAll: 'すべて',
      advisoryReviewed: 'reviewed',
      advisoryUnreviewed: 'unreviewed',
      source: 'ソース',
      sourcePlaceholder: '例: php, python, java',
      sourcePlaceholderGithub: '例: npm, pip, maven, go',
      sourcePlaceholderExploited: '例: linux, windows, php',
      search: '検索',
      clear: 'クリア',
      showing: '表示',
      of: '/',
      noResultsYet: '結果はまだありません',
      explain: '説明',
      explainTitle: 'このページについて',
      aboutPage: 'このページについて',
      gotIt: '了解',
      prev: '前へ',
      next: '次へ',
      page: 'ページ',
      loading: '脆弱性情報を読み込み中…',
      noMatch: '条件に一致する脆弱性はありません。',
      resetFilters: 'フィルターをリセット',
      showLess: '閉じる',
      showMore: 'もっと見る',
      moreCount: '+{n} 件',
      colId: 'CVE ID',
      colExploitId: 'CVE / EDB',
      copyCveId: 'CVE ID をコピー',
      cveIdCopied: 'CVE ID をコピーしました',
      colSeverity: '深刻度',
      colType: 'タイプ',
      colScore: 'スコア',
      colVerified: '検証済み',
      verifiedYes: 'はい',
      verifiedNo: 'いいえ',
      colCvss: 'CVSS',
      colAuthor: '作者',
      colCwes: 'CWE',
      colEdb: 'EDB ID',
      colPackages: 'パッケージ',
      colSource: 'ソース',
      colLanguages: '言語',
      colPlatform: 'プラットフォーム',
      colAdvisoryType: 'タイプ',
      colPublished: '公開日',
      colDescription: '説明',
      explainNvdTitle: 'CVE NVD',
      explainNvdSummary: 'NIST 国家脆弱性データベースの脆弱性を閲覧します。',
      explainNvd1: 'NVD は CVSS・深刻度・CWE・CPE パッケージ照合付きの CVE を公開します。',
      explainNvd2: 'キーワードでタイトルと説明を検索できます（製品名や XSS など）。',
      explainNvd3: '深刻度（CRITICAL / HIGH / MEDIUM / LOW）またはソース言語で絞り込めます。',
      explainNvd4: 'CVE ID を開くと公式 NVD 詳細ページを表示します。',
      explainMitreTitle: 'CVE MITRE',
      explainMitreSummary: 'MITRE CVE 辞書 — CVE 識別子の権威ある一覧を探索します。',
      explainMitre1: 'MITRE はセキュリティ業界で使われる CVE ID を割り当て・維持します。',
      explainMitre2: 'CVE レコードと関連する弱点コンテキストの照会に役立ちます。',
      explainMitre3: 'キーワード検索で一致する CVE を素早く見つけられます。',
      explainMitre4: '脆弱性の正規 CVE 識別が必要なときに使います。',
      explainCisaTitle: 'CVE CISA',
      explainCisaSummary: 'CISA 既知の悪用脆弱性（KEV）— 実環境で悪用されている問題を追跡します。',
      explainCisa1: 'CISA KEV は攻撃者に悪用されていることが分かっている CVE を強調します。',
      explainCisa2: '現実リスクが高いため、優先してパッチ適用してください。',
      explainCisa3: 'ベンダー・製品・深刻度で絞り込み・検索できます。',
      explainCisa4: '運用トリアージと修復計画に適しています。',
      explainGithubTitle: 'CVE GitHub',
      explainGithubSummary:
        'オープンソースパッケージ／エコシステムに紐づく GitHub セキュリティ勧告を確認します。',
      explainGithub1:
        '勧告には影響パッケージ、エコシステム（npm, pip, maven, go など）、範囲が含まれます。',
      explainGithub2: 'ソースフィルターは npm / pip / maven などのエコシステム名に適しています。',
      explainGithub3: 'パッケージチップは各勧告の脆弱な依存関係コンテキストを示します。',
      explainGithub4: 'ソフトウェアサプライチェーンのリスク評価に使います。',
      explainExploitedTitle: 'CVE Exploited',
      explainExploitedSummary: 'Exploit-DB の公開エクスプロイト（cve_exploited_db）を閲覧します。',
      explainExploited1: '各行はタイプ・プラットフォーム／ソース・作者・検証状態付きの記録です。',
      explainExploited2:
        'CVE ID（例: CVE-2009-3699）またはキーワードで検索し、タイプやソースで絞り込めます。',
      explainExploited3: 'ID リンクから Exploit-DB の詳細を開けます。',
      explainExploited4: '公開 PoC やエクスプロイト素材の検索に使います。'
    },
    login: {
      brand: 'Vulnerability',
      tagline: 'セキュリティインテリジェンス',
      point1Title: 'CVE 分析',
      point1Desc: 'NVD, MITRE, CISA, GitHub',
      point2Title: '脅威の可視化',
      point2Desc: '深刻度・パッケージ・勧告',
      signIn: 'サインイン',
      subtitle: 'アカウントで続行してください。',
      username: 'ユーザー名',
      usernamePlaceholder: 'メールまたはユーザー ID',
      password: 'パスワード',
      passwordPlaceholder: 'パスワードを入力',
      verification: '認証',
      solve: '計算',
      answer: '答え',
      refreshCaptcha: '認証を更新',
      remember: 'ログイン状態を保持',
      signingIn: 'サインイン中…',
      defaultMessage: 'このページを使うにはログインしてください。',
      usernameRequired: 'ユーザー名を入力してください',
      passwordRequired: 'パスワードを入力してください',
      captchaWrong: '認証の答えが正しくありません',
      loginFailed: 'ログインに失敗しました',
      noToken: 'ログイン成功しましたがトークンがありません'
    },
    createUser: {
      title: 'ユーザー作成',
      desc: '一時パスワードとヘッダー画像で新しいアカウントを追加します。',
      tip1: '初回ログイン用の一時パスワード',
      tip2: 'サインイン時にパスワード再設定が必要',
      tip3: 'アバターは上部ヘッダーに表示',
      viewList: 'ユーザー一覧を見る',
      headerPic: 'ヘッダー画像',
      headerPicHint: 'PNG / JPG · ログイン後に表示',
      select: '選択',
      clear: 'クリア',
      username: 'ユーザー名',
      usernamePlaceholder: 'userid',
      email: 'メール',
      emailPlaceholder: 'mail@example.com',
      password: 'パスワード（初回）',
      passwordPlaceholder: '6文字以上',
      note: '初回ログイン時に新しいパスワードの設定が求められます。',
      creating: '作成中…',
      submit: 'ユーザー作成'
    },
    userList: {
      title: 'ユーザー一覧',
      subtitle: '登録済みの全ユーザー',
      createUser: 'ユーザー作成',
      empty: 'ユーザーが見つかりません',
      admin: '管理者',
      user: 'ユーザー',
      reset: '再設定',
      yes: 'はい',
      no: 'いいえ',
      active: '有効',
      inactive: '無効',
      created: '作成',
      firstLogin: '初回ログイン',
      updated: '更新',
      delete: '削除',
      activate: '有効化'
    },
    attackCode: {
      button: '攻撃コード',
      title: '攻撃コード',
      kicker: '概念実証',
      loading: '攻撃コードを読み込み中…',
      empty: '攻撃コードの内容がありません。',
      unavailable: 'この CVE には攻撃コードがありません。',
      loadFailed: '攻撃コードの読み込みに失敗しました',
      close: '閉じる',
      copy: 'コピー',
      copied: 'コピー済み',
      summary: '概要',
      exploits: 'Exploit 参照',
      locations: '脆弱なコード位置',
      references: '参照リンク',
      exploitTag: 'Exploit',
      lines: '行',
      function: '関数',
      snippets: 'コードスニペット',
      rawSource: '生の PoC',
      locationHint: '位置ヒント',
      rawLink: 'Raw',
      sourceLink: 'ソース',
      codeFetched: 'コード取得済み',
      fetchFailed: '取得失敗',
      sourceLocations: 'ソースコード位置',
      cisaContext: 'CISA 情報',
      requiredAction: '必要な対応',
      ransomware: 'ランサムウェア利用',
      availCode: 'PoC コードあり',
      availReference: '参照のみ',
      availKnown: '悪用確認済み',
      availNone: 'PoC なし'
    },
    common: {
      na: 'N/A'
    }
  }
};



// WEBPACK FOOTER //
// ./src/locales/index.js